import { useEffect, useState } from "react";
import { DOCUMENT_DROPZONE_ACCEPT } from "shared/constants/officeFiles";
import { useFormik } from "formik";
import { RevisionClientResponseService } from "shared/services/revision";
import { toastMessage } from "shared/components/common/toast";
import { handleErrors } from "shared/utils/helper";
import { changeRequestValidation } from "shared/utils/validation";
import { ChangeRequestModalProps } from "shared/types/modal";
import { Icons } from "assets";
import classNames from "classnames";
import CustomModal from "shared/components/common/customModal";
import styles from "./style.module.scss";
import CustomPageHeading from "shared/components/common/customPageHeading";
import CustomTextEditor from "shared/components/common/customTextEditor";
import ImageItemsList from "shared/components/common/imageItemsList";
import ConfirmationModal from "shared/modal/confirmation";

const ChangeRequestModal = ({
  isOpen,
  onClose,
  data,
  updateData,
  title,
  description,
  icon,
  iconStyle,
  iconContainerStyle,
  buttonText,
  revisionId,
  onSuccess,
}: ChangeRequestModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const formik = useFormik({
    initialValues: {
      clientComment: "",
      imageItems: [
        {
          id: "1",
          imageLabel: "",
          notes: "",
          hasImage: false,
          file: null as File | null,
          imageUrl: undefined as string | undefined,
        },
      ],
    },
    validationSchema: changeRequestValidation,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: false,
    onSubmit: async (values) => {
      if (!revisionId) {
        toastMessage("error", "Revision ID is required");
        return;
      }

      try {
        setIsSubmitting(true);

        const formData = new FormData();
        formData.append("client_comment", values.clientComment.trim());

        const itemsWithFiles = values.imageItems.filter(
          (item) => item.hasImage && item.file instanceof File,
        );

        if (itemsWithFiles.length > 0) {
          itemsWithFiles.forEach((item, index) => {
            formData.append(
              `inspirations[${index}][label]`,
              item.imageLabel || "Untitled",
            );
            formData.append(
              `inspirations[${index}][additional_notes]`,
              item.notes || "",
            );
            if (item.file instanceof File) {
              formData.append(
                `inspirations[${index}][inspiration_images]`,
                item.file,
              );
            }
          });
        }

        const response = await RevisionClientResponseService(
          revisionId,
          formData,
        );

        if (response.status === 200 || response.status === 201) {
          toastMessage("success", "Changes submitted successfully!");
          onSuccess?.();
          formik.resetForm();
          onClose();
        }
      } catch (error: any) {
        handleErrors(error);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // Cancel, the X and the backdrop all land here; the modal stays mounted, so
  // without this the next open still shows the last request's text and files.
  const handleClose = () => {
    formik.resetForm();
    setShowConfirmModal(false);
    onClose();
  };

  useEffect(() => {
    updateData?.({
      imageItems: formik.values.imageItems,
      imageLabel: formik.values.imageItems[0]?.imageLabel || "",
      notes: formik.values.imageItems[0]?.notes || "",
    });
  }, [formik.values.imageItems, updateData]);

  const handleEditorChange = (id: string, value: string) => {
    const updatedItems = formik.values.imageItems.map((item) =>
      item.id === id ? { ...item, notes: value } : item,
    );
    formik.setFieldValue("imageItems", updatedItems);
  };

  const handleAddMore = () => {
    const newId = (formik.values.imageItems.length + 1).toString();
    formik.setFieldValue("imageItems", [
      ...formik.values.imageItems,
      {
        id: newId,
        imageLabel: "",
        notes: "",
        hasImage: false,
        file: null,
        imageUrl: undefined,
      },
    ]);
  };

  const handleDelete = (id: string) => {
    if (formik.values.imageItems.length > 1) {
      const updatedItems = formik.values.imageItems.filter(
        (item) => item.id !== id,
      );
      formik.setFieldValue("imageItems", updatedItems);
    }
  };

  const handleImageLabelChange = (id: string, value: string) => {
    const updatedItems = formik.values.imageItems.map((item) =>
      item.id === id ? { ...item, imageLabel: value } : item,
    );
    formik.setFieldValue("imageItems", updatedItems);
  };

  const handleFileUpload = (id: string, file: File, preview: string) => {
    const updatedItems = formik.values.imageItems.map((item) =>
      item.id === id
        ? {
            ...item,
            file,
            hasImage: true,
            imageUrl: preview,
          }
        : item,
    );
    formik.setFieldValue("imageItems", updatedItems);
  };

  const handleSubmitWithValidation = async () => {
    formik.setTouched({
      clientComment: true,
      imageItems: formik.values.imageItems.map(() => ({
        imageLabel: true,
        notes: true,
        hasImage: true,
        file: true,
      })),
    });

    const errors = await formik.validateForm();

    if (Object.keys(errors).length > 0) {
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = () => {
    setShowConfirmModal(false);
    formik.handleSubmit();
  };

  return (
    <>
    <CustomModal
      size="md"
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      description={description}
      icon={icon}
      iconStyle={iconStyle}
      iconContainerStyle={iconContainerStyle}
      showBackButton={false}
      customContentContainer="noSidePadding"
      actionButtonText={buttonText}
      actionButtonIcon={Icons.BlueFileUpload}
      actionButtonContainerStyle={styles.submitBtn}
      actionButtonIconStyle={styles.submitIcon}
      showModalHeader
      showModalFooter
      onNext={handleSubmitWithValidation}
      actionButtonLoading={isSubmitting}
      actionButtonDisabled={isSubmitting}
    >
      <div className={classNames(styles.modalContentContainer)}>
        <div className={classNames(styles.main, "flex flex-col gap-6")}>
          <div className={classNames(styles.content, "")}>
            <div className="flex gap-2">
              <Icons.IdeaIcon className={styles.icon} />
              <p>
                Tip: Don't worry if the first design isn't perfect — that's part
                of the process! Submit all your changes at once to keep
                revisions efficient.
              </p>
            </div>
          </div>

          <CustomPageHeading
            title="Describe what you’d like to change"
            description="Be as specific as possible. Include room names, dimensions, or reference specific areas of the plans."
            size="large"
          />

          <CustomTextEditor
            value={formik.values.clientComment}
            customContainerStyle={styles.commentsEditor}
            customToolbarStyle={styles.commentsToolbar}
            onChange={(value) => formik.setFieldValue("clientComment", value)}
            placeholder="Type your message here..."
          />
          {formik.touched.clientComment && formik.errors.clientComment && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.clientComment}
            </div>
          )}

          <CustomPageHeading
            title="Attach reference files or sketches"
            size="large"
          />
          {formik.touched.imageItems &&
            formik.errors.imageItems &&
            typeof formik.errors.imageItems === "string" && (
              <div className="text-red-500 text-sm mt-2">
                {formik.errors.imageItems}
              </div>
            )}

          <div className="">
            <ImageItemsList
              imageItems={formik.values.imageItems}
              errors={(() => {
                const errorObj: Record<string, string> = {};
                formik.values.imageItems.forEach((item, index) => {
                  if (
                    formik.touched.imageItems?.[index] &&
                    formik.errors.imageItems?.[index] &&
                    typeof formik.errors.imageItems[index] === "object"
                  ) {
                    const itemErrors = formik.errors.imageItems[index] as any;
                    if (itemErrors.imageLabel) {
                      errorObj[`imageLabel-${item.id}`] = itemErrors.imageLabel;
                    }
                    if (itemErrors.notes) {
                      errorObj[`notes-${item.id}`] = itemErrors.notes;
                    }
                  }
                });
                return errorObj;
              })()}
              onFileChange={(id, file, preview) => {
                if (file && preview) {
                  handleFileUpload(id, file, preview);
                }
              }}
              onImageLabelChange={(id, value) => {
                handleImageLabelChange(id, value);
                const index = formik.values.imageItems.findIndex(
                  (item) => item.id === id,
                );
                if (index !== -1) {
                  formik.setFieldTouched(
                    `imageItems.${index}.imageLabel`,
                    true,
                  );
                }
              }}
              onNotesChange={handleEditorChange}
              onAddMore={handleAddMore}
              onDelete={handleDelete}
              notesRequired={true}
              notesLabel="Additional Notes"
              showShadow={false}
              showPadding={false}
              // smallImageUpload={true}
              accept={DOCUMENT_DROPZONE_ACCEPT}
            />
          </div>
        </div>
      </div>
    </CustomModal>
    <ConfirmationModal
      show={showConfirmModal}
      handleClose={() => setShowConfirmModal(false)}
      handleSubmit={handleConfirmSubmit}
      title="Submit Revision Request?"
      subtitle="Are you sure this is your complete set of changes for this revision? Once you hit submit, you will not be able to add to this list."
      actionButtonText="Yes, submit"
      cancelButtonText="Cancel"
      modalType="confirmation"
      loading={isSubmitting}
    />
    </>
  );
};

export default ChangeRequestModal;
